import { useId, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { trackRecordAPI } from '../../services/api'
import type { TradeDirection, TradeRecord } from '../../types'
import useEscapeKey from '../../hooks/useEscapeKey'
import { tradeLevelError } from '../../utils/tradeLevels'
import Spinner from '../Spinner'

const toDatetimeLocal = (date: Date) => {
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`
}

interface LogTradeModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: (trade: TradeRecord) => void
}

const LogTradeModal = ({ isOpen, onClose, onCreated }: LogTradeModalProps) => {
  const [symbol, setSymbol] = useState('')
  const [direction, setDirection] = useState<TradeDirection>('LONG')
  const [entryPrice, setEntryPrice] = useState('')
  const [entryTime, setEntryTime] = useState(() => toDatetimeLocal(new Date()))
  const [stopLoss, setStopLoss] = useState('')
  const [takeProfit, setTakeProfit] = useState('')
  const [timeframe, setTimeframe] = useState('')
  const [strategyName, setStrategyName] = useState('')
  const [saving, setSaving] = useState(false)
  const titleId = useId()

  const resetForm = () => {
    setSymbol('')
    setDirection('LONG')
    setEntryPrice('')
    setEntryTime(toDatetimeLocal(new Date()))
    setStopLoss('')
    setTakeProfit('')
    setTimeframe('')
    setStrategyName('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  useEscapeKey(handleClose, isOpen)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const parsedEntryPrice = parseFloat(entryPrice)
    if (!symbol.trim() || isNaN(parsedEntryPrice)) {
      toast.error('Please enter a symbol and a valid entry price')
      return
    }

    if (new Date(entryTime).getTime() > Date.now()) {
      toast.error('Entry time cannot be in the future')
      return
    }

    const parsedStopLoss = stopLoss ? parseFloat(stopLoss) : undefined
    const parsedTakeProfit = takeProfit ? parseFloat(takeProfit) : undefined
    const levelError = tradeLevelError(direction, parsedEntryPrice, parsedStopLoss, parsedTakeProfit)
    if (levelError) {
      toast.error(levelError)
      return
    }

    setSaving(true)
    try {
      const trade = await trackRecordAPI.createTrade({
        symbol: symbol.trim().toUpperCase(),
        direction,
        entry_price: parsedEntryPrice,
        entry_time: new Date(entryTime).toISOString(),
        stop_loss: parsedStopLoss,
        take_profit: parsedTakeProfit,
        timeframe: timeframe.trim() || undefined,
        strategy_name: strategyName.trim() || undefined,
      })
      toast.success('Trade logged successfully!')
      onCreated(trade)
      resetForm()
      onClose()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to log trade')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 id={titleId} className="text-xl font-bold text-gray-900">Log Trade</h2>
          <button
            onClick={handleClose}
            className="text-gray-600 hover:text-gray-800 transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="trade-symbol" className="block text-sm font-medium text-gray-700 mb-1">
                Symbol
              </label>
              <input
                id="trade-symbol"
                type="text"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                placeholder="BTC/USDT"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="trade-direction" className="block text-sm font-medium text-gray-700 mb-1">
                Direction
              </label>
              <select
                id="trade-direction"
                value={direction}
                onChange={(e) => setDirection(e.target.value as TradeDirection)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="LONG">Long</option>
                <option value="SHORT">Short</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="trade-entry-price" className="block text-sm font-medium text-gray-700 mb-1">
                Entry Price
              </label>
              <input
                id="trade-entry-price"
                type="number"
                step="any"
                min="0"
                value={entryPrice}
                onChange={(e) => setEntryPrice(e.target.value)}
                placeholder="60000"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="trade-entry-time" className="block text-sm font-medium text-gray-700 mb-1">
                Entry Time
              </label>
              <input
                id="trade-entry-time"
                type="datetime-local"
                value={entryTime}
                onChange={(e) => setEntryTime(e.target.value)}
                max={toDatetimeLocal(new Date())}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="trade-stop-loss" className="block text-sm font-medium text-gray-700 mb-1">
                Stop Loss (optional)
              </label>
              <input
                id="trade-stop-loss"
                type="number"
                step="any"
                min="0"
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
                placeholder="58000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="trade-take-profit" className="block text-sm font-medium text-gray-700 mb-1">
                Take Profit (optional)
              </label>
              <input
                id="trade-take-profit"
                type="number"
                step="any"
                min="0"
                value={takeProfit}
                onChange={(e) => setTakeProfit(e.target.value)}
                placeholder="65000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="trade-timeframe" className="block text-sm font-medium text-gray-700 mb-1">
                Timeframe (optional)
              </label>
              <input
                id="trade-timeframe"
                type="text"
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                placeholder="4H"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="trade-strategy" className="block text-sm font-medium text-gray-700 mb-1">
                Strategy (optional)
              </label>
              <input
                id="trade-strategy"
                type="text"
                value={strategyName}
                onChange={(e) => setStrategyName(e.target.value)}
                placeholder="Breakout"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving && <Spinner size="sm" className="border-white border-t-transparent" />}
              {saving ? 'Saving...' : 'Log Trade'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LogTradeModal
